import {
  useEffect,
  useRef,
  useCallback,
  type MouseEvent as ReactMouseEvent,
} from 'react';
import { Box, Flex } from '@chakra-ui/react';
import { KText } from '@/ui-library/components/text';
import './coach.scss';

export interface MomentSeek {
  seconds: number;
  ts: string;
  // Viewport rect of the clicked pill, used to anchor the popover.
  x: number;
  top: number;
  bottom: number;
  // Increments on every click so re-clicking the same timestamp still re-seeks.
  token: number;
}

const WIDTH = 360;
const HEIGHT = 244;
const GAP = 10;

const Close = (p: any) => (
  <svg
    viewBox="0 0 24 24"
    width="13"
    height="13"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    {...p}
  >
    <path d="M18 6 6 18M6 6l12 12" />
  </svg>
);

function place(seek: MomentSeek) {
  const vw = window.innerWidth;
  const left = Math.min(Math.max(seek.x - WIDTH / 2, 12), vw - WIDTH - 12);
  // Prefer above the pill; flip below when there isn't room.
  const above = seek.top - HEIGHT - GAP >= 8;
  const top = above ? seek.top - HEIGHT - GAP : seek.bottom + GAP;
  return { left, top };
}

/**
 * A small floating recording player anchored to a clicked timestamp. The <video> is mounted hidden
 * the moment the slider opens with `preload="metadata"`, so the recording's header is already warm
 * by the first click and the seek fires instantly (no first-click metadata-load lag — only the file
 * header is fetched, never the whole MP4). It's never unmounted, so re-clicking a timestamp only
 * repositions the popover and sets `currentTime` — every jump is instant. The interviewer watches the
 * moment in place without leaving the coaching note.
 */
export function CoachMomentPlayer({
  recordingUrl,
  seek,
  onClose,
}: {
  recordingUrl: string;
  seek: MomentSeek | null;
  onClose: () => void;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);

  // Same recording as the modal player, so it needs the same block on the
  // browser's "Save Video As…".
  const blockNativeMenu = useCallback((event: ReactMouseEvent) => {
    event.preventDefault();
  }, []);
  const wrapRef = useRef<HTMLDivElement>(null);
  const visible = Boolean(seek);

  // Seek + play whenever a new moment is requested (token changes).
  useEffect(() => {
    const video = videoRef.current;
    if (!seek || !video) return;
    const run = () => {
      try {
        video.currentTime = seek.seconds;
        void video.play();
      } catch {
        /* play() can reject if interrupted; the controls still let the user start it */
      }
    };
    if (video.readyState >= 1) run();
    else video.addEventListener('loadedmetadata', run, { once: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seek?.token]);

  // Pause when closed so audio doesn't keep playing behind the note.
  useEffect(() => {
    if (!visible) videoRef.current?.pause();
  }, [visible]);

  // Release the media element on unmount — slider close OR SPA navigation away. Clearing src + load()
  // frees the buffered video and its network connection immediately rather than leaving it to GC, so
  // memory doesn't creep as the user moves between interviews.
  useEffect(() => {
    const video = videoRef.current;
    return () => {
      if (!video) return;
      video.pause();
      video.removeAttribute('src');
      video.load();
    };
  }, []);

  // Close on outside click (but not when clicking another timestamp pill — that re-seeks) or Escape.
  useEffect(() => {
    if (!visible) return;
    const onDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (wrapRef.current?.contains(target) || target.closest('.tstamp'))
        return;
      onClose();
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [visible, onClose]);

  // Off-screen while idle so the warm, hidden <video> keeps preloading without showing.
  const pos = seek ? place(seek) : { left: -9999, top: -9999 };

  return (
    <Box
      ref={wrapRef}
      className={`coach-moment ${visible ? 'is-visible' : ''}`}
      style={{ left: pos.left, top: pos.top, width: WIDTH }}
    >
      <Flex className="coach-moment-head" align="center" gap="8px">
        <Box className="coach-moment-dot" />
        <KText
          size="xs-semi-bold"
          color="base-white"
          fontSize="11.5px"
          fontWeight="600"
        >
          Recording · {seek?.ts}
        </KText>
        <Box flex={1} />
        <Flex
          as="button"
          className="coach-moment-x"
          align="center"
          justify="center"
          onClick={onClose}
          aria-label="Close"
        >
          <Close />
        </Flex>
      </Flex>
      <video
        ref={videoRef}
        className="coach-moment-video"
        src={recordingUrl}
        preload="metadata"
        controls
        playsInline
        disablePictureInPicture
        controlsList="nodownload noplaybackrate"
        onContextMenu={blockNativeMenu}
      />
    </Box>
  );
}

export default CoachMomentPlayer;
